import React, { useState, useCallback } from 'react';
import { Grid } from '@material-ui/core';

import { searchHinovaAssociado, searchHinovaHistorico, listHinovaAssociados } from '~/services/api';
import { handleApiErrorResponse } from '~/services/handleErrors';
import { HinovaAssociado, HinovaHistorico } from '~/types';
import { Table } from '~/components/Table';
import InputComponent from '~/components/InputComponent';
import Button from '~/components/Button';
import Snackbar from '~/components/Snackbar/index';
import { SeverityType } from '~/components/Snackbar/index';
import AssociadoDetails from '../AssociadoDetails';

export default function AssociadoSearch() {
  const [cpf, setCpf] = useState<string>('');
  const [associados, setAssociados] = useState<HinovaAssociado[]>([]);
  const [associado, setAssociado] = useState<HinovaAssociado | null>(null);
  const [historico, setHistorico] = useState<HinovaHistorico[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [messageSnack, setMessageSnack] = useState('');
  const [severity, setSeverity] = useState<SeverityType>('info');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openAssociadoDetails, setOpenAssociadoDetails] = useState<boolean>(false);

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return value;
  };

  const searchAssociado = useCallback(async () => {
    try {
      setLoading(true);
      let response;

      if (cpf.trim()) {
        // Busca por CPF específico
        const cpfNumbers = cpf.replace(/\D/g, '');
        const [associadoResponse, historicoResponse] = await Promise.all([
          searchHinovaAssociado(cpfNumbers),
          searchHinovaHistorico(cpfNumbers),
        ]);

        if (associadoResponse.status === 200 && associadoResponse.data) {
          const data = associadoResponse.data;
          const associadoData = Array.isArray(data) ? data[0] : data;
          setAssociado(associadoData);
          setAssociados(Array.isArray(data) ? data : [associadoData]);
        } else {
          setAssociado(null);
          setAssociados([]);
        }

        if (historicoResponse.status === 200 && historicoResponse.data) {
          const data = historicoResponse.data;
          setHistorico(Array.isArray(data) ? data : data.historico || []);
        } else {
          setHistorico([]);
        }

        if (associadoResponse.status === 200) {
          setOpenAssociadoDetails(true);
        }
      } else {
        // Lista todos os associados
        response = await listHinovaAssociados();

        if (response.status === 200 && response.data) {
          const data = response.data;
          const associadosData = Array.isArray(data) ? data : [];
          setAssociados(associadosData);
        } else {
          setAssociados([]);
        }
      }
    } catch (err) {
      const error = handleApiErrorResponse(err);
      setSeverity('warning');
      setMessageSnack(
        (error?.applicationErrorMessage || error?.apiError) + ': não foi possível buscar associados. Tente novamente.',
      );
      setOpenSnackbar(true);
      setAssociados([]);
      setAssociado(null);
      setHistorico([]);
    } finally {
      setLoading(false);
    }
  }, [cpf]);

  const handleViewDetails = (associadoData: HinovaAssociado) => {
    setAssociado(associadoData);
    // Buscar histórico se tiver CPF
    if (associadoData.cpf) {
      const cpfNumbers = associadoData.cpf.replace(/\D/g, '');
      searchHinovaHistorico(cpfNumbers)
        .then(historicoResponse => {
          if (historicoResponse.status === 200 && historicoResponse.data) {
            const data = historicoResponse.data;
            setHistorico(Array.isArray(data) ? data : data.historico || []);
          }
        })
        .catch(() => {
          setHistorico([]);
        });
    }
    setOpenAssociadoDetails(true);
  };

  return (
    <>
      <Grid container spacing={2} style={{ marginBottom: 20 }} alignItems='flex-end'>
        <Grid item xs={12} sm={6} md={4}>
          <InputComponent
            label='CPF'
            name='cpf'
            type='text'
            value={cpf}
            onChange={e => {
              const formatted = formatCPF(e.target.value);
              setCpf(formatted);
            }}
            error={null}
            placeholder='Ex: 999.999.999-99'
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Button
            label='Buscar'
            loading={loading}
            onClick={searchAssociado}
            filled
            style={{ width: '100%', marginTop: 0 }}
          />
        </Grid>
      </Grid>

      <Table
        isLoading={loading}
        data={associados}
        columns={{
          cpf: {
            title: 'CPF',
            render: rowData => rowData.cpf || '-',
          },
          nome: {
            title: 'Nome',
            render: rowData => rowData.nome || '-',
          },
          email: {
            title: 'Email',
            render: rowData => rowData.email || '-',
          },
          telefone: {
            title: 'Telefone',
            render: rowData => rowData.telefone || '-',
          },
          cooperativa: {
            title: 'Cooperativa',
            render: rowData => rowData.cooperativa || '-',
          },
          situacao: {
            title: 'Situação',
            render: rowData => rowData.situacao || '-',
          },
        }}
        actions={[
          {
            icon: () => <span>👁️</span>,
            tooltip: 'Ver detalhes',
            onClick: (_, rowData) => handleViewDetails(rowData as HinovaAssociado),
          },
        ]}
        localization={{
          body: {
            emptyDataSourceMessage: loading
              ? 'Carregando...'
              : 'Nenhum associado encontrado. Use o campo de busca para pesquisar por CPF ou deixe vazio para listar todos.',
          },
          pagination: {
            labelRowsSelect: 'associados',
          },
        }}
        options={{
          showTitle: false,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          paging: true,
          filtering: false,
          search: false,
        }}
      />

      {associado && (
        <AssociadoDetails
          associado={associado}
          historico={historico}
          open={openAssociadoDetails}
          onClose={() => {
            setOpenAssociadoDetails(false);
            setAssociado(null);
            setHistorico([]);
          }}
        />
      )}

      <Snackbar message={messageSnack} open={openSnackbar} severity={severity} onClose={() => setOpenSnackbar(false)} />
    </>
  );
}
